import { useStoreContact } from "@/context/StoreContactContext";
import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Trash2, CheckCircle2 } from "lucide-react";
import { LegalLayout } from "@/components/LegalLayout";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/data";

export default function DeleteAccount() {
  const contact = useStoreContact();
  const [form, setForm] = useState({ name: "", email: "", phone: "", reason: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/deletion-request", form);
      setDone(true);
      toast.success("Your request has been submitted.");
    } catch {
      toast.error("Something went wrong. Please try again or email us.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-foreground/20 py-3 text-foreground placeholder:text-muted-foreground/70 focus:border-accent focus:outline-none transition-colors duration-300";

  return (
    <LegalLayout title="Delete Account & Data" updated="August 2026">
      <p className="text-lg text-foreground leading-relaxed" data-testid="delete-intro">
        You have the right to request deletion of your {BRAND.name} account and all personal data
        associated with it. Submit the form below and our team will verify and process your request
        within <strong className="text-foreground">30 days</strong>.
      </p>

      <div className="grid sm:grid-cols-3 gap-6 border-t border-border pt-10">
        {[
          { icon: Trash2, t: "What we delete", d: "Your name, email, phone, delivery details, order history and any messages you sent us." },
          { icon: ShieldCheck, t: "What may be kept", d: "Records we are legally required to retain (e.g. billing/tax records), only for as long as the law requires." },
          { icon: CheckCircle2, t: "Timeline", d: "Requests are verified and completed within 30 days. You'll get an email confirmation." },
        ].map((c) => (
          <div key={c.t} className="space-y-3">
            <span className="w-10 h-10 flex items-center justify-center bg-foreground text-white"><c.icon size={17} /></span>
            <h3 className="font-display text-xl tracking-tight text-foreground">{c.t}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{c.d}</p>
          </div>
        ))}
      </div>

      <div className="border-t border-border pt-10">
        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-border p-8 sm:p-10 text-center"
            data-testid="delete-success"
          >
            <CheckCircle2 className="mx-auto text-accent mb-4" size={40} />
            <h2 className="font-display text-3xl tracking-tight text-foreground">Request received</h2>
            <p className="mt-3 text-muted-foreground max-w-md mx-auto">
              We've emailed a confirmation to <strong className="text-foreground">{form.email}</strong>.
              Your data will be deleted within 30 days.{contact.phone && <> For urgent help, call {contact.phone}.</>}
            </p>
          </motion.div>
        ) : (
          <div className="bg-white border border-border p-8 sm:p-10 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)]">
            <h2 className="font-display text-3xl tracking-tight text-foreground mb-8">
              Request account deletion
            </h2>
            <form onSubmit={submit} className="space-y-7" data-testid="delete-form">
              <div className="grid sm:grid-cols-2 gap-7">
                <input className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} data-testid="delete-name" />
                <input className={inputCls} type="email" placeholder="Account email *" value={form.email} onChange={set("email")} data-testid="delete-email" />
              </div>
              <input className={inputCls} placeholder="Phone number (optional)" value={form.phone} onChange={set("phone")} data-testid="delete-phone" />
              <textarea className={`${inputCls} resize-none`} rows={4} placeholder="Reason for deletion (optional)" value={form.reason} onChange={set("reason")} data-testid="delete-reason" />
              <p className="text-xs text-muted-foreground">
                By submitting, you confirm this is your account. This action is permanent once processed.
              </p>
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-3 bg-accent text-white py-4 text-xs font-mono-label uppercase tracking-widest hover:bg-foreground transition-colors duration-300 disabled:opacity-60"
                data-testid="delete-submit"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={15} />}
                {loading ? "Submitting..." : "Request Deletion"}
              </button>
            </form>
          </div>
        )}
      </div>

      {contact.email && <div className="border-t border-border pt-10 text-muted-foreground text-sm leading-relaxed">
        Prefer to email us directly? Write to{" "}
        <a href={`mailto:${contact.email}`} className="text-accent hover:underline">{contact.email}</a>{" "}
        with the subject "Account Deletion" from your registered email address.
      </div>}
    </LegalLayout>
  );
}
